'use client';

import { HTMLAttributes, lazy, Suspense } from 'react';

interface GeneratedPathProps {
  fileName: string;
  serviceName?: 'common' | string;
  src?: never;
}

interface FullUrlProps {
  src: string;
  fileName?: never;
  serviceName?: never;
}

type ResourceProps = GeneratedPathProps | FullUrlProps;

interface Props {
  loop?: boolean;
  autoPlay?: boolean;
  className?: HTMLAttributes<HTMLDivElement>['className'];
}

type LottiePlayerProps = ResourceProps & Props;

export type { ResourceProps, GeneratedPathProps, FullUrlProps };

const RESOURCE_CENTER_BASE_URL = process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL || 'https://prd.cdn.bznav.com';

const getResourceSrc = (props: ResourceProps): string => {
  if ('src' in props && props.src) return props.src;

  const { fileName, serviceName = 'common' } = props;

  return `${RESOURCE_CENTER_BASE_URL}/${serviceName}/animation/${fileName}`;
};

const DotLottieReact = lazy(() =>
  import('@lottiefiles/dotlottie-react').then((m) => ({
    default: m.DotLottieReact
  }))
);

const WebPlayer = lazy(() =>
  import('@lottiefiles/react-lottie-player').then((m) => ({
    default: m.Player
  }))
);

/**
 * Lottie 애니메이션 플레이어 컴포넌트
 *
 * ### 사용 방식
 * 이 컴포넌트는 아래 두 가지 방식 중 하나로만 사용하도록 설계되었습니다.
 *
 * 1) `serviceName` + `fileName`
 * - 디자인 시스템에서 정의한 리소스 센터 경로 규칙을 따릅니다.
 * - 내부적으로 `${RESOURCE_CENTER_BASE_URL}/{serviceName}/animation/{fileName}` 형태의 URL을 생성합니다.
 *
 * 2) `src`
 * - 외부 리소스 또는 자유로운 경로의 Full URL을 직접 전달할 수 있습니다.
 *
 * ⚠️ 위 두 방식은 서로 동시에 사용할 수 없으며,
 * 잘못된 조합은 타입 레벨에서 에러로 제한됩니다.
 *
 * @param {object} props - LottiePlayerProps
 * @param {string} [props.fileName] - 리소스센터의 파일명 (src가 없을 때 필수)
 * @param {string} [props.serviceName='common'] - 서비스 이름. 리소스센터 경로 생성 시 사용. 기본값은 'common'
 * @param {string} [props.src] - Full URL 경로 (fileName, serviceName과 함께 사용 불가)
 * @param {boolean} [props.loop=true] - 애니메이션 반복 여부
 * @param {boolean} [props.autoPlay=true] - 애니메이션 자동 재생 여부
 * @param {string} [props.className] - CSS 클래스 이름
 *
 * @example 디자인 시스템 리소스 사용
 * <LottiePlayer fileName="animation.json" serviceName="care" />
 *
 * @example Full URL 직접 사용
 * <LottiePlayer src="https://example.com/animation.json" />
 */
export const LottiePlayer = (props: LottiePlayerProps) => {
  const { loop = true, autoPlay = true, className } = props;
  const src = getResourceSrc(props);

  return (
    <Suspense fallback={null}>
      <DotLottieReact src={src} loop={loop} autoplay={autoPlay} renderConfig={{ autoResize: true }} className={className} />
    </Suspense>
  );
};

/**
 * LottieWebPlayer는 로띠 애니메이션 내부적인 이미지 로딩을 위해 사용됩니다.
 */
export const LottieWebPlayer = (props: LottiePlayerProps) => {
  const { loop = true, autoPlay = true, className } = props;
  const src = getResourceSrc(props);

  return (
    <Suspense fallback={null}>
      <WebPlayer autoplay={autoPlay} loop={loop} src={src} className={className} />
    </Suspense>
  );
};
